import React, { useState } from "react";
import { Syringe, Activity, Stethoscope, Leaf, Sparkles, Check, ArrowRight } from "lucide-react";
import { SectionHeading } from "../site/SectionHeading";
import { SERVICE_CATEGORIES, COSMETIC_BRANDS } from "@/lib/site-data";

const ICONS = { Syringe, Activity, Stethoscope, Leaf, Sparkles };

export const Services = () => {
    const [active, setActive] = useState(SERVICE_CATEGORIES[0].key);
    const current = SERVICE_CATEGORIES.find((c) => c.key === active) || SERVICE_CATEGORIES[0];
    const Icon = ICONS[current.icon] || Sparkles;

    return (
        <section
            id="services"
            className="relative py-24 lg:py-32 bg-brand-cream border-y border-brand-line"
            data-testid="services-section"
        >
            <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-12">
                    <SectionHeading
                        eyebrow="Nos prestations"
                        title={<>Une offre <span className="italic text-brand-primary">complète</span>, sous un même toit.</>}
                    />
                    <p className="max-w-md font-sans text-brand-ink/65 leading-relaxed">
                        De la délivrance d'ordonnance à la vaccination, des dépistages aux médecines complémentaires —
                        choisissez une catégorie pour explorer ce que nous proposons.
                    </p>
                </div>

                {/* Category tabs */}
                <div
                    className="flex flex-wrap gap-2 mb-8 border-b border-brand-line"
                    data-testid="services-tabs"
                >
                    {SERVICE_CATEGORIES.map((cat) => {
                        const CatIcon = ICONS[cat.icon] || Sparkles;
                        const isActive = cat.key === active;
                        return (
                            <button
                                key={cat.key}
                                onClick={() => setActive(cat.key)}
                                className={`inline-flex items-center gap-2 px-4 py-3 -mb-px border-b-2 transition-colors font-sans text-sm ${
                                    isActive
                                        ? "border-brand-primary text-brand-primary"
                                        : "border-transparent text-brand-ink/55 hover:text-brand-ink"
                                }`}
                                data-testid={`services-tab-${cat.key}`}
                            >
                                <CatIcon className="h-4 w-4" />
                                {cat.title}
                            </button>
                        );
                    })}
                </div>

                {/* Content panel */}
                <article
                    key={current.key}
                    className="bg-white border border-brand-line p-8 lg:p-12 animate-fade-up"
                    data-testid={`services-panel-${current.key}`}
                >
                    <div className="flex items-start gap-5 mb-6">
                        <div className="h-12 w-12 rounded-full bg-brand-primary/10 text-brand-primary inline-flex items-center justify-center shrink-0">
                            <Icon className="h-5 w-5" />
                        </div>
                        <div>
                            <h3 className="font-display text-2xl lg:text-3xl text-brand-ink leading-tight">{current.title}</h3>
                            <p className="mt-2 font-sans text-brand-ink/65 max-w-2xl">{current.intro}</p>
                        </div>
                    </div>

                    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 mt-6">
                        {current.items.map((it, idx) => {
                            const isObj = typeof it === "object" && it !== null;
                            const key = isObj ? it.title : it;
                            return (
                                <li
                                    key={`${key}-${idx}`}
                                    className={`flex items-start gap-3 text-sm text-brand-ink/80 font-sans ${isObj ? "sm:col-span-2 lg:col-span-3" : ""}`}
                                >
                                    <Check className="h-4 w-4 text-brand-primary mt-0.5 shrink-0" />
                                    {isObj ? (
                                        <div>
                                            <p className="text-brand-ink font-medium">{it.title}</p>
                                            <p className="mt-1 text-brand-ink/65 leading-relaxed">{it.description}</p>
                                        </div>
                                    ) : (
                                        <span>{it}</span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>

                    {current.footnote && (
                        <p className="mt-6 text-xs text-brand-ink/55 italic font-sans">{current.footnote}</p>
                    )}

                    <div className="mt-8 pt-6 border-t border-brand-line flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <p className="font-sans text-sm text-brand-ink/60">
                            Une question sur cette prestation ?
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a href="/rendez-vous" className="ll-btn ll-btn-primary text-sm" data-testid="services-rdv-btn">
                                Prendre rendez-vous
                                <ArrowRight className="h-4 w-4" />
                            </a>
                            <a href="#contact" className="ll-btn ll-btn-ghost text-sm">
                                Nous contacter
                            </a>
                        </div>
                    </div>
                </article>

                {/* Cosmetic brands */}
                <div className="mt-16" data-testid="cosmetic-brands">
                    <div className="text-center max-w-2xl mx-auto mb-8">
                        <p className="ll-eyebrow text-brand-primary mb-3">Gammes cosmétiques</p>
                        <h3 className="font-display text-2xl lg:text-3xl text-brand-ink leading-tight" data-testid="brands-title">
                            Des marques <span className="italic text-brand-primary">choisies avec soin</span>
                        </h3>
                        <p className="mt-3 font-sans text-sm text-brand-ink/60" data-testid="brands-subtitle">
                            Dermocosmétique, soins capillaires, ongles et bien-être : découvrez les marques que nous
                            avons sélectionnées pour vous.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {COSMETIC_BRANDS.map((b) => (
                            <div
                                key={b.name}
                                className="border border-brand-line bg-white h-24 flex items-center justify-center px-3 hover:border-brand-primary/50 transition-colors group overflow-hidden"
                                title={b.name}
                                data-testid={`brand-${b.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            >
                                {b.logo ? (
                                    <img
                                        src={b.logo}
                                        alt={b.name}
                                        loading="lazy"
                                        className="max-h-16 max-w-full w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                                    />
                                ) : (
                                    <span className="font-display text-base lg:text-lg text-brand-ink/80 group-hover:text-brand-primary text-center">
                                        {b.name}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Services;
